/**
 * ALTIORA · Digital Twin · Le jumeau numérique du conseiller (Sprint 7)
 *
 * 3 blocs:
 *   1. Progression & complétude du profil
 *   2. Formulaire structuré (identité, licences, questionnaire, documents)
 *   3. Audit 360° + Copilote quotidien (brief matinal)
 */
import { useEffect, useState, useCallback } from "react";
import {
  User, Sparkles, FileText, Award, RefreshCw, Save, Plus, Trash2,
  Wand2, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Card, Section, PrimaryButton, GhostButton,
  DangerButton, FormField, Input, Textarea, Select, Loader, TabsShell,
  Alert, Badge, Metric,
} from "@/components/altiora/PremiumUI";
import { useUserLocation } from "@/hooks/useUserLocation";


export default function DigitalTwin() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("profile");
  const [audit, setAudit] = useState(null);
  const [brief, setBrief] = useState(null);
  const [busyAudit, setBusyAudit] = useState(false);
  const [busyBrief, setBusyBrief] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get("/digital_twin/profile"),
        api.get("/digital_twin/audit/latest").catch(() => ({ data: null })),
      ]);
      setProfile(p.data);
      setAudit(a.data);
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/digital_twin/profile", {
        first_name: profile.first_name, last_name: profile.last_name,
        display_name: profile.display_name, email: profile.email,
        phone: profile.phone, firm: profile.firm, city: profile.city,
        province: profile.province, licences: profile.licences,
        questionnaire: profile.questionnaire,
      });
      setProfile(data);
      toast.success(`Profil enregistré · ${data.completion_pct}% complété`);
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const runAudit = async () => {
    setBusyAudit(true);
    try {
      const { data } = await api.post("/digital_twin/audit/run");
      setAudit(data);
      const nonEmpty = data.sections.filter(s => s.diagnostic).length;
      if (nonEmpty === 0) {
        toast.warning("Audit lancé mais retourné vide (crédit IA épuisé ?). Réessayez plus tard.");
      } else {
        toast.success(`Audit 360° généré · ${nonEmpty}/${data.sections.length} sections`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusyAudit(false); }
  };

  const runBrief = async () => {
    setBusyBrief(true);
    try {
      const { data } = await api.get("/digital_twin/brief");
      setBrief(data);
      toast.success("Brief matinal généré");
    } catch { toast.error("Erreur"); }
    finally { setBusyBrief(false); }
  };

  if (loading) return <PageShell><div className="py-16 flex justify-center"><Loader /></div></PageShell>;
  if (!profile) return null;

  return (
    <PageShell testid="digital-twin-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Sprint 7 · Conseiller Digital Twin"
          title="Votre jumeau numérique"
          subtitle="Plus votre profil est riche, plus l'IA vous accompagne finement. Chaque champ affine les recommandations quotidiennes."
          testid="dt-header"
          actions={
            <>
              <GhostButton onClick={load} testid="dt-refresh">
                <RefreshCw size={13} /> Rafraîchir
              </GhostButton>
              <PrimaryButton onClick={save} disabled={saving} testid="dt-save">
                <Save size={13} /> {saving ? "Enregistrement…" : "Enregistrer"}
              </PrimaryButton>
            </>
          }
        />

        {/* Completion header */}
        <Card className="p-4 mb-5" testid="dt-completion-card">
          <div className="flex items-center gap-4">
            <div className="font-serif text-4xl" style={{ color: "#C5A880" }}>
              {profile.completion_pct}%
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest font-semibold"
                   style={{ color: "#0B2545" }}>Complétude du profil</div>
              <div className="text-[12px]" style={{ color: "#94A3B8" }}>
                {profile.onboarding_done
                  ? "Profil validé · le copilote IA est activé"
                  : "Complétez au moins 60% pour activer le copilote quotidien"}
              </div>
              <div className="mt-2 h-2 rounded-full" style={{ background: "#F1F5F9" }}>
                <div className="h-full rounded-full transition-all duration-500"
                     style={{ width: `${profile.completion_pct}%`,
                                background: profile.completion_pct >= 60 ? "#16A34A" : "#C5A880" }}
                     data-testid="dt-completion-bar" />
              </div>
            </div>
          </div>
        </Card>

        <TabsShell
          testid="dt-tabs"
          tabs={[
            { id: "profile",       label: "Identité & Licences", icon: User },
            { id: "questionnaire", label: "Questionnaire intelligent", icon: Sparkles },
            { id: "documents",     label: "Documents", icon: FileText },
            { id: "audit",         label: "Audit 360°", icon: Award },
            { id: "brief",         label: "Copilote quotidien" },
          ]}
          active={tab}
          onChange={setTab}
        />

        {tab === "profile"       && <ProfileTab profile={profile} setProfile={setProfile} />}
        {tab === "questionnaire" && <QuestionnaireTab profile={profile} setProfile={setProfile} />}
        {tab === "documents"     && <DocumentsTab profile={profile} onChanged={load} />}
        {tab === "audit"         && <AuditTab audit={audit} onRun={runAudit} busy={busyAudit}
                                                    completion={profile.completion_pct} />}
        {tab === "brief"         && <BriefTab brief={brief} onRun={runBrief} busy={busyBrief} />}
      </div>
    </PageShell>
  );
}


function ProfileTab({ profile, setProfile }) {
  const upd = (k, v) => setProfile({ ...profile, [k]: v });
  return (
    <Card className="p-4" testid="dt-profile-card">
      <Section title="Identité professionnelle">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Prénom" required testid="dt-first-name">
            {({ id }) => <Input id={id} value={profile.first_name || ""}
              onChange={(e) => upd("first_name", e.target.value)}
              testid="dt-first-name-input" />}
          </FormField>
          <FormField label="Nom" required testid="dt-last-name">
            {({ id }) => <Input id={id} value={profile.last_name || ""}
              onChange={(e) => upd("last_name", e.target.value)}
              testid="dt-last-name-input" />}
          </FormField>
          <FormField label="Courriel" testid="dt-email">
            {({ id }) => <Input id={id} type="email" value={profile.email || ""}
              onChange={(e) => upd("email", e.target.value)}
              testid="dt-email-input" />}
          </FormField>
          <FormField label="Téléphone" testid="dt-phone">
            {({ id }) => <Input id={id} value={profile.phone || ""}
              onChange={(e) => upd("phone", e.target.value)}
              testid="dt-phone-input" />}
          </FormField>
          <FormField label="Cabinet / Firme" testid="dt-firm">
            {({ id }) => <Input id={id} value={profile.firm || ""}
              onChange={(e) => upd("firm", e.target.value)}
              testid="dt-firm-input" />}
          </FormField>
          <FormField label="Ville" testid="dt-city">
            {({ id }) => <Input id={id} value={profile.city || ""}
              onChange={(e) => upd("city", e.target.value)}
              testid="dt-city-input" />}
          </FormField>
        </div>

        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest font-semibold mb-2"
               style={{ color: "#0B2545" }}>Licences & permis</div>
          <div className="space-y-2">
            {(profile.licences || []).map((l, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-2 rounded-lg p-3 bg-white"
                   style={{ border: "1px solid #C5A88033" }}
                   data-testid={`dt-licence-${i}`}>
                <Input value={l.kind}
                  placeholder="AMF / LLQP / CFP"
                  onChange={(e) => {
                    const next = [...profile.licences];
                    next[i] = { ...next[i], kind: e.target.value };
                    upd("licences", next);
                  }} testid={`dt-licence-kind-${i}`} />
                <Input value={l.number || ""} placeholder="N°"
                  onChange={(e) => {
                    const next = [...profile.licences];
                    next[i] = { ...next[i], number: e.target.value };
                    upd("licences", next);
                  }} testid={`dt-licence-number-${i}`} />
                <Input type="date" value={l.expires_at || ""}
                  onChange={(e) => {
                    const next = [...profile.licences];
                    next[i] = { ...next[i], expires_at: e.target.value };
                    upd("licences", next);
                  }} testid={`dt-licence-expires-${i}`} />
                <DangerButton onClick={() => {
                  const next = profile.licences.filter((_, idx) => idx !== i);
                  upd("licences", next);
                }} testid={`dt-licence-remove-${i}`}>
                  <Trash2 size={12} />
                </DangerButton>
              </div>
            ))}
          </div>
          <GhostButton onClick={() => upd("licences",
              [...(profile.licences || []), { kind: "", number: "", expires_at: null }])}
              testid="dt-licence-add">
            <Plus size={13} /> Ajouter une licence
          </GhostButton>
        </div>
      </Section>
    </Card>
  );
}


function QuestionnaireTab({ profile, setProfile }) {
  const { city: userCity, label: userLabel } = useUserLocation();
  const territoryHint = userCity ? `${userCity}, environs` : (userLabel || "");
  const q = profile.questionnaire || {};
  const updQ = (k, v) => setProfile({
    ...profile, questionnaire: { ...q, [k]: v },
  });
  const csv = (v) => (v || []).join(", ");
  const parseCsv = (s) => s.split(",").map(x => x.trim()).filter(Boolean);
  return (
    <Card className="p-4" testid="dt-questionnaire-card">
      <Section title="Questionnaire intelligent"
               hint="Ces réponses affinent chaque recommandation quotidienne.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Années d'expérience CSF" testid="dt-q-years">
            {({ id }) => <Input id={id} type="number" min="0"
              value={q.csf_years || 0}
              onChange={(e) => updQ("csf_years", parseInt(e.target.value, 10) || 0)}
              testid="dt-q-years-input" />}
          </FormField>
          <FormField label="Objectif de revenu annuel ($)" testid="dt-q-goal">
            {({ id }) => <Input id={id} type="number" min="0" step="1000"
              value={q.financial_goal_year || 0}
              onChange={(e) => updQ("financial_goal_year", parseFloat(e.target.value) || 0)}
              testid="dt-q-goal-input" />}
          </FormField>
          <FormField label="Spécialités (séparées par virgule)" testid="dt-q-specialties">
            {({ id }) => <Input id={id} value={csv(q.specialties)}
              placeholder="retraite, entrepreneurs, jeunes familles"
              onChange={(e) => updQ("specialties", parseCsv(e.target.value))}
              testid="dt-q-specialties-input" />}
          </FormField>
          <FormField label="Produits maîtrisés" testid="dt-q-products">
            {({ id }) => <Input id={id} value={csv(q.products_mastered)}
              placeholder="life_insurance, savings_reer_celi, disability"
              onChange={(e) => updQ("products_mastered", parseCsv(e.target.value))}
              testid="dt-q-products-input" />}
          </FormField>
          <FormField label="Territoire" testid="dt-q-territory">
            {({ id }) => <Input id={id} value={csv(q.territory)}
              placeholder="Montréal, Laval, Rive-Sud"
              onChange={(e) => updQ("territory", parseCsv(e.target.value))}
              testid="dt-q-territory-input" />}
          </FormField>
          <FormField label="Langues" testid="dt-q-languages">
            {({ id }) => <Input id={id} value={csv(q.languages)}
              placeholder="fr, en"
              onChange={(e) => updQ("languages", parseCsv(e.target.value))}
              testid="dt-q-languages-input" />}
          </FormField>
          <FormField label="Situation familiale" testid="dt-q-family">
            {({ id }) => <Input id={id} value={q.family_situation || ""}
              onChange={(e) => updQ("family_situation", e.target.value)}
              testid="dt-q-family-input" />}
          </FormField>
          <FormField label="Disponibilité" testid="dt-q-avail">
            {({ id }) => <Input id={id} value={q.availability || ""}
              placeholder="temps plein / 40h/sem"
              onChange={(e) => updQ("availability", e.target.value)}
              testid="dt-q-avail-input" />}
          </FormField>
        </div>
        <FormField label="Passions / centres d'intérêt (séparés par virgule)"
                   testid="dt-q-passions">
          {({ id }) => <Input id={id} value={csv(q.passions)}
            onChange={(e) => updQ("passions", parseCsv(e.target.value))}
            testid="dt-q-passions-input" />}
        </FormField>
        <FormField label="Forces (séparées par virgule)" testid="dt-q-strengths">
          {({ id }) => <Input id={id} value={csv(q.strengths)}
            placeholder="closing, empathie, écoute"
            onChange={(e) => updQ("strengths", parseCsv(e.target.value))}
            testid="dt-q-strengths-input" />}
        </FormField>
        <FormField label="Faiblesses" testid="dt-q-weaknesses">
          {({ id }) => <Input id={id} value={csv(q.weaknesses)}
            placeholder="prospection à froid, fiscalité avancée"
            onChange={(e) => updQ("weaknesses", parseCsv(e.target.value))}
            testid="dt-q-weaknesses-input" />}
        </FormField>
        <FormField label="Ambitions de carrière" testid="dt-q-ambitions">
          {({ id }) => <Textarea id={id} rows={3} value={q.career_ambitions || ""}
            onChange={(e) => updQ("career_ambitions", e.target.value)}
            testid="dt-q-ambitions-input" />}
        </FormField>
        <FormField label="Habitudes de vie / bien-être" testid="dt-q-lifestyle">
          {({ id }) => <Textarea id={id} rows={2} value={q.lifestyle || ""}
            onChange={(e) => updQ("lifestyle", e.target.value)}
            testid="dt-q-lifestyle-input" />}
        </FormField>
      </Section>
    </Card>
  );
}


function DocumentsTab({ profile, onChanged }) {
  const [form, setForm] = useState({ name: "", kind: "cv", url: "" });
  const add = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    await api.post("/digital_twin/documents", form);
    toast.success("Document ajouté");
    setForm({ name: "", kind: "cv", url: "" });
    onChanged();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    await api.delete(`/digital_twin/documents/${id}`);
    toast.success("Supprimé");
    onChanged();
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <Card className="p-4" testid="dt-doc-add-card">
        <Section title="Ajouter un document">
          <FormField label="Nom" required testid="dt-doc-name">
            {({ id }) => <Input id={id} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              testid="dt-doc-name-input" />}
          </FormField>
          <FormField label="Type" testid="dt-doc-kind">
            {({ id }) => (
              <Select id={id} value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                testid="dt-doc-kind-select">
                <option value="cv">CV</option>
                <option value="amf_permit">Permis AMF</option>
                <option value="certificate">Attestation / Certificat</option>
                <option value="business_plan">Plan d'affaires</option>
                <option value="objective">Objectifs personnels</option>
                <option value="other">Autre</option>
              </Select>
            )}
          </FormField>
          <FormField label="URL du document" testid="dt-doc-url">
            {({ id }) => <Input id={id} value={form.url}
              placeholder="https://drive.google.com/…"
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              testid="dt-doc-url-input" />}
          </FormField>
          <PrimaryButton onClick={add} testid="dt-doc-add-submit">
            <Plus size={13} /> Ajouter
          </PrimaryButton>
        </Section>
      </Card>
      <Card className="p-4 lg:col-span-2" testid="dt-doc-list-card">
        <Section title="Vos documents"
                 hint={`${(profile.documents || []).length} document(s)`}>
          {profile.missing_documents?.length > 0 && (
            <Alert variant="warning" title="Documents recommandés manquants"
                   testid="dt-doc-missing">
              <ul className="list-disc ml-4">
                {profile.missing_documents.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </Alert>
          )}
          <ul className="space-y-2 mt-3">
            {(profile.documents || []).map((d) => (
              <li key={d.id}
                  data-testid={`dt-doc-${d.id}`}
                  className="flex items-center justify-between rounded-lg p-2.5 bg-white"
                  style={{ border: "1px solid #C5A88033" }}>
                <div>
                  <div className="text-[13px] font-semibold" style={{ color: "#0B2545" }}>
                    {d.name}
                  </div>
                  <div className="text-[11px]" style={{ color: "#94A3B8" }}>
                    {d.kind} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("fr-CA") : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {d.url && (
                    <a href={d.url} target="_blank" rel="noreferrer"
                       className="text-[11px] underline"
                       style={{ color: "#0B2545" }}
                       data-testid={`dt-doc-open-${d.id}`}>Ouvrir</a>
                  )}
                  <DangerButton onClick={() => remove(d.id)}
                                testid={`dt-doc-remove-${d.id}`}>
                    <Trash2 size={12} />
                  </DangerButton>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      </Card>
    </div>
  );
}


function AuditTab({ audit, onRun, busy, completion }) {
  const ready = completion >= 30;
  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <PrimaryButton onClick={onRun} disabled={busy || !ready}
                       testid="dt-audit-run">
          <Wand2 size={13} /> {busy ? "Analyse en cours…" : "Générer l'audit 360°"}
        </PrimaryButton>
        {!ready && (
          <span className="text-[11px]" style={{ color: "#94A3B8" }}>
            Complétez au moins 30% du profil pour activer l'audit.
          </span>
        )}
      </div>
      {!audit && (
        <Card className="p-6 text-center" testid="dt-audit-empty">
          <div className="font-serif text-lg mb-1" style={{ color: "#0B2545" }}>
            Aucun audit encore généré
          </div>
          <div className="text-[12px]" style={{ color: "#94A3B8" }}>
            Une fois lancé, l'audit analyse 10 dimensions et livre une feuille de route priorisée.
          </div>
        </Card>
      )}
      {audit && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {audit.sections.map((s) => (
              <Card key={s.key} className="p-4" testid={`dt-audit-${s.key}`}>
                <div className="text-[10px] uppercase tracking-widest font-semibold mb-1"
                     style={{ color: "#C5A880" }}>{s.title}</div>
                <div className="text-[13px] mb-3" style={{ color: "#334155" }}>
                  {s.diagnostic || "— Données insuffisantes ou budget IA épuisé —"}
                </div>
                {s.strengths?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-[10px] font-semibold" style={{ color: "#16A34A" }}>
                      Forces</div>
                    <ul className="text-[11px] list-disc ml-4" style={{ color: "#334155" }}>
                      {s.strengths.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                )}
                {s.risks?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-[10px] font-semibold" style={{ color: "#B91C1C" }}>
                      Risques</div>
                    <ul className="text-[11px] list-disc ml-4" style={{ color: "#334155" }}>
                      {s.risks.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                )}
                {s.priority_actions?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold" style={{ color: "#0B2545" }}>
                      Actions prioritaires</div>
                    <ol className="text-[11px] list-decimal ml-4" style={{ color: "#334155" }}>
                      {s.priority_actions.map((x, i) => <li key={i}>{x}</li>)}
                    </ol>
                  </div>
                )}
              </Card>
            ))}
          </div>
          {audit.roadmap?.length > 0 && (
            <Card className="p-4 mt-4" testid="dt-audit-roadmap">
              <Section title="Feuille de route priorisée">
                <ol className="space-y-2">
                  {audit.roadmap.map((r, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Badge variant="gold">{r.period}</Badge>
                      <div className="flex-1 text-[12px]">
                        <div style={{ color: "#0B2545", fontWeight: 600 }}>{r.action}</div>
                        <div style={{ color: "#94A3B8" }}>{r.why}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </Section>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


function BriefTab({ brief, onRun, busy }) {
  return (
    <div>
      <div className="mb-4">
        <PrimaryButton onClick={onRun} disabled={busy} testid="dt-brief-run">
          <Sparkles size={13} /> {busy ? "Génération…" : "Générer le brief du jour"}
        </PrimaryButton>
      </div>
      {!brief && (
        <Card className="p-6 text-center" testid="dt-brief-empty">
          <div className="text-[12px]" style={{ color: "#94A3B8" }}>
            Cliquez pour que votre copilote IA rédige le brief matinal — 10 sections d'actions concrètes.
          </div>
        </Card>
      )}
      {brief && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {Object.entries(brief.brief).map(([k, section]) => (
            <Card key={k} className="p-4" testid={`dt-brief-${k}`}>
              <div className="text-[13px] font-semibold mb-2"
                   style={{ color: "#0B2545" }}>{section.title}</div>
              {section.items.length === 0 ? (
                <div className="text-[11px] italic" style={{ color: "#94A3B8" }}>
                  Rien à signaler.
                </div>
              ) : (
                <ul className="space-y-1.5 text-[12px]" style={{ color: "#334155" }}>
                  {section.items.map((x, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <ChevronRight size={12} className="mt-0.5 shrink-0"
                                     style={{ color: "#C5A880" }} />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
